import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useCurrentAccount } from '@mysten/dapp-kit';
import { RiPhoneLine, RiMicLine, RiMicOffLine, RiLoader4Line, RiShieldCheckLine, RiVolumeUpLine, RiRefreshLine } from 'react-icons/ri';
import { initiateCall, answerCall } from '../utils/voiceCall';
import { useCall } from '../context/CallContext';

const styles = `
  @keyframes vcPulseRing {
    0% { transform: scale(1); opacity: 1; }
    100% { transform: scale(1.7); opacity: 0; }
  }
  @keyframes vcSpin {
    to { transform: rotate(360deg); }
  }
  @keyframes vcFadeIn {
    from { opacity: 0; transform: scale(0.95); }
    to { opacity: 1; transform: scale(1); }
  }
`;

function AudioBars({ stream, isMuted }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const audioCtxRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width;
    const H = canvas.height;
    const barCount = 5;
    const barW = 6;
    const gap = (W - barCount * barW) / (barCount + 1);

    const drawFlat = () => {
      ctx.clearRect(0, 0, W, H);
      for (let i = 0; i < barCount; i++) {
        const x = gap + i * (barW + gap);
        const y = (H - 4) / 2;
        ctx.fillStyle = 'rgba(0,255,255,0.2)';
        ctx.fillRect(x, y, barW, 4);
      }
    };

    if (!stream || isMuted) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (audioCtxRef.current) {
        try { audioCtxRef.current.close(); } catch (_) {}
        audioCtxRef.current = null;
      }
      drawFlat();
      return;
    }

    let audioCtx;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = audioCtx;
      // Resume if suspended (required on mobile)
      if (audioCtx.state === 'suspended') audioCtx.resume();

      const src = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 32;
      analyser.smoothingTimeConstant = 0.7;
      src.connect(analyser);

      const dataArr = new Uint8Array(analyser.frequencyBinCount);

      const draw = () => {
        if (!canvasRef.current) return;
        analyser.getByteFrequencyData(dataArr);
        ctx.clearRect(0, 0, W, H);

        for (let i = 0; i < barCount; i++) {
          const val = dataArr[i * 2] || 0;
          const barH = Math.max(4, (val / 255) * H);
          const x = gap + i * (barW + gap);
          const y = (H - barH) / 2;

          const grad = ctx.createLinearGradient(0, y, 0, y + barH);
          grad.addColorStop(0, '#ff00ff');
          grad.addColorStop(1, '#00ffff');
          ctx.fillStyle = grad;
          // roundRect with fallback for older browsers
          if (typeof ctx.roundRect === 'function') {
            ctx.beginPath();
            ctx.roundRect(x, y, barW, barH, 3);
            ctx.fill();
          } else {
            ctx.fillRect(x, y, barW, barH);
          }
        }

        rafRef.current = requestAnimationFrame(draw);
      };
      draw();
    } catch (e) {
      drawFlat();
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { if (audioCtxRef.current) audioCtxRef.current.close(); } catch (_) {}
      audioCtxRef.current = null;
    };
  }, [stream, isMuted]);

  return (
    <canvas
      ref={canvasRef}
      width={60}
      height={28}
      style={{ display: 'block' }}
    />
  );
}

export default function VoiceCallModal() {
  const currentAccount = useCurrentAccount();
  const { activeCall, endActiveCall } = useCall();

  // States: requesting | mic_denied | ringing | connecting | connected | ended
  const [callState, setCallState] = useState('requesting');
  const [isMuted, setIsMuted] = useState(false);
  const [duration, setDuration] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [localStream, setLocalStream] = useState(null);
  const [remoteStream, setRemoteStream] = useState(null);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const audioRef = useRef(null);
  const cleanupRef = useRef(null);
  const timerRef = useRef(null);
  const pcRef = useRef(null);
  const mountedRef = useRef(true);
  const wakeLockRef = useRef(null);
  const localStreamRef = useRef(null); // tracks the actual MediaStream for cleanup

  const acquireWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator) {
      try { wakeLockRef.current = await navigator.wakeLock.request('screen'); } catch (_) {}
    }
  }, []);

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
  }, []);

  const stopLocalStream = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(t => t.stop());
      localStreamRef.current = null;
    }
    setLocalStream(null);
  }, []);

  const handleEnd = useCallback(() => {
    if (!mountedRef.current) return;
    setCallState('ended');
    clearInterval(timerRef.current);
    releaseWakeLock();
    stopLocalStream();
    if (cleanupRef.current) {
      try { cleanupRef.current(); } catch (_) {}
      cleanupRef.current = null;
    }
    if (pcRef.current) {
      try { pcRef.current.close(); } catch (_) {}
      pcRef.current = null;
    }
    if (audioRef.current) {
      audioRef.current.srcObject = null;
    }
    setTimeout(() => { if (mountedRef.current) endActiveCall(); }, 800);
  }, [endActiveCall, releaseWakeLock, stopLocalStream]);

  // Mount/unmount cleanup
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearInterval(timerRef.current);
      releaseWakeLock();
      stopLocalStream();
      if (cleanupRef.current) {
        try { cleanupRef.current(); } catch (_) {}
      }
    };
  }, [releaseWakeLock, stopLocalStream]);

  // Main call effect — runs on new call or retry
  useEffect(() => {
    if (!activeCall || !currentAccount?.address) return;

    const myAddress = currentAccount.address;
    const { otherAddress, mode, sessionId } = activeCall;

    const run = async () => {
      // ── Step 1: Acquire microphone ──
      setCallState('requesting');
      setErrorMsg('');
      setAutoplayBlocked(false);

      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      } catch (err) {
        if (!mountedRef.current) return;
        // Check if permanently blocked vs. just dismissed
        let permState = 'prompt';
        try {
          const perm = await navigator.permissions.query({ name: 'microphone' });
          permState = perm.state;
        } catch (_) {}

        setCallState('mic_denied');
        setErrorMsg(
          permState === 'denied'
            ? 'Microphone is blocked by the browser. Tap the lock icon (🔒) in your address bar → set Microphone to "Allow" → tap Retry.'
            : 'Microphone access denied. Please allow microphone when the browser prompts and tap Retry.'
        );
        return;
      }

      if (!mountedRef.current) { stream.getTracks().forEach(t => t.stop()); return; }

      // Stream acquired — show bars immediately
      localStreamRef.current = stream;
      setLocalStream(stream);

      // ── Step 2: Callbacks ──
      const onStream = (remStream) => {
        if (!mountedRef.current) return;
        setRemoteStream(remStream);
        if (audioRef.current) {
          audioRef.current.srcObject = remStream;
          audioRef.current.play().catch(() => {
            if (mountedRef.current) setAutoplayBlocked(true);
          });
        }
      };

      const onConnect = () => {
        if (!mountedRef.current) return;
        setCallState('connected');
        acquireWakeLock();
        let secs = 0;
        timerRef.current = setInterval(() => {
          if (!mountedRef.current) return;
          secs++;
          setDuration(secs);
        }, 1000);
      };

      const onClose = () => {
        if (mountedRef.current) handleEnd();
      };

      const onError = (err) => {
        if (!mountedRef.current) return;
        setErrorMsg(err.message || 'Call failed');
        setCallState('ended');
        setTimeout(() => { if (mountedRef.current) endActiveCall(); }, 3000);
      };

      // ── Step 3: Initiate or answer ──
      try {
        if (mode === 'outgoing') {
          setCallState('ringing');
          const result = await initiateCall({
            myAddress,
            recipientAddress: otherAddress,
            stream,
            onStream, onConnect, onClose, onError,
            onRinging: () => { if (mountedRef.current) setCallState('ringing'); },
          });
          if (!mountedRef.current) { result.cleanup(); return; }
          pcRef.current = result.pc;
          cleanupRef.current = result.cleanup;
        } else {
          setCallState('connecting');
          const result = await answerCall({
            myAddress,
            callerAddress: otherAddress,
            sessionId,
            stream,
            onStream, onConnect, onClose, onError,
          });
          if (!mountedRef.current) { result.cleanup(); return; }
          pcRef.current = result.pc;
          cleanupRef.current = result.cleanup;
        }
      } catch (err) {
        if (!mountedRef.current) return;
        stopLocalStream();
        setErrorMsg(err.message || 'Could not start call');
        setCallState('ended');
        setTimeout(() => { if (mountedRef.current) endActiveCall(); }, 3000);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCall?.otherAddress, activeCall?.mode, currentAccount?.address, retryCount]);

  // Sync audio element if remoteStream arrives before audioRef is ready
  useEffect(() => {
    if (audioRef.current && remoteStream) {
      audioRef.current.srcObject = remoteStream;
      audioRef.current.play().catch(() => {
        if (mountedRef.current) setAutoplayBlocked(true);
      });
    }
  }, [remoteStream]);

  const toggleMute = () => {
    if (localStream) {
      const nextMuted = !isMuted;
      localStream.getAudioTracks().forEach(t => { t.enabled = !nextMuted; });
      setIsMuted(nextMuted);
    }
  };

  const tapToPlay = () => {
    if (audioRef.current) {
      audioRef.current.play()
        .then(() => setAutoplayBlocked(false))
        .catch(() => {});
    }
  };

  const handleRetryMic = () => {
    setErrorMsg('');
    setCallState('requesting');
    setRetryCount(c => c + 1);
  };

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  if (!activeCall) return null;

  const { otherName, mode } = activeCall;
  const isConnecting = callState === 'ringing' || callState === 'connecting' || callState === 'requesting';
  const showAudioBars = localStream && callState !== 'ended' && callState !== 'requesting' && callState !== 'mic_denied';

  const stateLabel = {
    requesting: 'REQUESTING MIC...',
    mic_denied: 'MIC BLOCKED',
    ringing: 'RINGING...',
    connecting: 'CONNECTING...',
    connected: formatTime(duration),
    ended: errorMsg || 'CALL ENDED',
  }[callState] || '';

  const accentColor = callState === 'connected' ? '#00ffff' : callState === 'mic_denied' ? '#ff4444' : '#ff00ff';

  return (
    <>
      <style>{styles}</style>
      {/* Hidden audio element for remote stream */}
      <audio ref={audioRef} autoPlay playsInline style={{ display: 'none' }} />

      <div style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'linear-gradient(135deg, #050010 0%, #0d001f 60%, #050010 100%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'Orbitron, sans-serif',
        animation: 'vcFadeIn 0.25s ease-out',
        overflowY: 'auto',
        padding: '20px 0',
      }}>
        {/* Background grid */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(0,255,255,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }} />

        {/* Avatar + pulsing ring */}
        <div style={{ position: 'relative', marginBottom: '32px' }}>
          {isConnecting && callState !== 'mic_denied' && (
            <>
              <div style={{
                position: 'absolute', inset: '-20px',
                borderRadius: '50%', border: `2px solid ${accentColor}`,
                animation: 'vcPulseRing 1.6s ease-out infinite',
              }} />
              <div style={{
                position: 'absolute', inset: '-20px',
                borderRadius: '50%', border: `2px solid ${accentColor}`,
                animation: 'vcPulseRing 1.6s ease-out 0.8s infinite',
              }} />
            </>
          )}
          {callState === 'connected' && (
            <div style={{
              position: 'absolute', inset: '-8px',
              borderRadius: '50%', border: '2px solid #00ffff',
              boxShadow: '0 0 20px #00ffff60',
            }} />
          )}
          <div style={{
            width: '88px', height: '88px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #1a0033, #330066)',
            border: `3px solid ${accentColor}`,
            boxShadow: `0 0 30px ${accentColor}40`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            position: 'relative', zIndex: 1,
          }}>
            <span style={{ fontSize: '36px', color: accentColor }}>
              {callState === 'mic_denied' ? '🎤' : (otherName ? otherName[0].toUpperCase() : '?')}
            </span>
          </div>
        </div>

        {/* Name */}
        <div style={{ fontSize: '18px', color: '#00ffff', textShadow: '0 0 12px #00ffff', marginBottom: '8px', letterSpacing: '2px' }}>
          {callState === 'mic_denied' ? 'MIC ACCESS NEEDED' : otherName}
        </div>

        {/* Direction label */}
        {callState !== 'mic_denied' && (
          <div style={{ fontSize: '11px', color: '#ff00ff', opacity: 0.8, marginBottom: '16px', letterSpacing: '3px' }}>
            {mode === 'outgoing' ? 'OUTGOING CALL' : 'INCOMING CALL'}
          </div>
        )}

        {/* State / timer */}
        <div style={{
          fontSize: callState === 'connected' ? '28px' : '14px',
          color: callState === 'connected' ? '#00ffff' : callState === 'mic_denied' ? '#ff4444' : '#ffffff',
          textShadow: callState === 'connected' ? '0 0 16px #00ffff' : 'none',
          marginBottom: '20px', letterSpacing: callState === 'connected' ? '4px' : '1px',
          display: 'flex', alignItems: 'center', gap: '8px',
        }}>
          {isConnecting && callState !== 'mic_denied' && (
            <RiLoader4Line style={{ animation: 'vcSpin 1s linear infinite' }} />
          )}
          {stateLabel}
        </div>

        {/* Mic denied: instructions + retry */}
        {callState === 'mic_denied' && (
          <div style={{
            background: 'rgba(255,0,0,0.08)', border: '1px solid rgba(255,68,68,0.4)',
            borderRadius: '12px', padding: '16px 20px', maxWidth: '300px',
            textAlign: 'center', marginBottom: '24px',
          }}>
            <div style={{ color: '#ff8888', fontSize: '11px', lineHeight: '1.7', marginBottom: '16px' }}>
              {errorMsg}
            </div>
            <button
              onClick={handleRetryMic}
              style={{
                background: 'rgba(0,255,255,0.12)', border: '1.5px solid #00ffff',
                borderRadius: '8px', color: '#00ffff', fontFamily: 'Orbitron, sans-serif',
                fontSize: '12px', padding: '8px 20px', cursor: 'pointer', letterSpacing: '1px',
                display: 'flex', alignItems: 'center', gap: '6px', margin: '0 auto',
              }}
            >
              <RiRefreshLine size={14} /> RETRY
            </button>
          </div>
        )}

        {/* Audio bars — shown as soon as mic is acquired (ringing, connecting, connected) */}
        {showAudioBars && (
          <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '8px', color: 'rgba(0,255,255,0.5)', marginBottom: '4px', letterSpacing: '1px' }}>YOU</div>
              <AudioBars stream={localStream} isMuted={isMuted} />
            </div>
            {remoteStream && (
              <>
                <div style={{ width: '1px', height: '28px', background: 'rgba(255,255,255,0.15)' }} />
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: '8px', color: 'rgba(0,255,255,0.5)', marginBottom: '4px', letterSpacing: '1px' }}>THEM</div>
                  <AudioBars stream={remoteStream} isMuted={false} />
                </div>
              </>
            )}
          </div>
        )}

        {/* Tap to play — shown when browser blocks autoplay */}
        {autoplayBlocked && (
          <button
            onClick={tapToPlay}
            style={{
              background: 'rgba(255,255,0,0.12)', border: '1px solid #ffff00',
              borderRadius: '8px', color: '#ffff00', fontFamily: 'Orbitron, sans-serif',
              fontSize: '11px', padding: '8px 16px', cursor: 'pointer', marginBottom: '16px',
              display: 'flex', alignItems: 'center', gap: '6px',
            }}
          >
            <RiVolumeUpLine size={14} /> TAP TO HEAR AUDIO
          </button>
        )}

        {/* Security badge */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: '6px',
          background: 'rgba(0,255,255,0.07)', border: '1px solid rgba(0,255,255,0.25)',
          borderRadius: '20px', padding: '5px 14px', marginBottom: '36px',
          fontSize: '9px', color: '#00ffff', letterSpacing: '1px',
        }}>
          <RiShieldCheckLine size={12} />
          E2E ENCRYPTED · DTLS-SRTP · P2P
        </div>

        {/* Controls */}
        <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
          {/* Mute — available as soon as mic is acquired */}
          {localStream && callState !== 'ended' && callState !== 'requesting' && callState !== 'mic_denied' && (
            <button
              onClick={toggleMute}
              title={isMuted ? 'Unmute' : 'Mute'}
              style={{
                width: '56px', height: '56px', borderRadius: '50%',
                background: isMuted ? 'rgba(255,0,255,0.25)' : 'rgba(0,255,255,0.15)',
                border: `2px solid ${isMuted ? '#ff00ff' : '#00ffff'}`,
                color: isMuted ? '#ff00ff' : '#00ffff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', transition: 'all 0.2s ease',
                boxShadow: isMuted ? '0 0 15px #ff00ff40' : '0 0 15px #00ffff40',
              }}
            >
              {isMuted ? <RiMicOffLine size={22} /> : <RiMicLine size={22} />}
            </button>
          )}

          {/* End call */}
          <button
            onClick={handleEnd}
            title="End call"
            style={{
              width: '68px', height: '68px', borderRadius: '50%',
              background: 'linear-gradient(135deg, #cc0000, #ff2222)',
              border: '3px solid #ff4444',
              color: '#ffffff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', transition: 'all 0.2s ease',
              boxShadow: '0 0 25px rgba(255,0,0,0.5)',
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.1)'; e.currentTarget.style.boxShadow = '0 0 35px rgba(255,0,0,0.7)'; }}
            onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 0 25px rgba(255,0,0,0.5)'; }}
          >
            <RiPhoneLine size={28} style={{ transform: 'rotate(135deg)' }} />
          </button>
        </div>

        {/* General error message (not mic_denied) */}
        {errorMsg && callState !== 'mic_denied' && (
          <div style={{ marginTop: '20px', color: '#ff4444', fontSize: '12px', textAlign: 'center', maxWidth: '300px' }}>
            {errorMsg}
          </div>
        )}
      </div>
    </>
  );
}
